import { loadScopeInfo, saveScopeInfo } from './store';

// Sealed at rest (D281, issue #84): what this device keeps between visits
// (drafts, the search index) is sealed with a key that never leaves it, as
// the phones' chat cache (D273) and outbox (D274) are. The key is an
// AES-GCM CryptoKey made non-extractable and kept in the device store
// (IndexedDB holds it, but no script can read its bytes). Without it (storage
// blocked, a private window) nothing is written at all: never plain text.

const SCOPE = 'rest-key';
const PREFIX = 'r1.';
const IV_BYTES = 12;

const keys = new Map<string, Promise<CryptoKey | null>>();

async function loadOrMake(userId: string): Promise<CryptoKey | null> {
  if (typeof indexedDB === 'undefined' || !globalThis.crypto?.subtle) {
    return null;
  }
  try {
    const kept = await loadScopeInfo<CryptoKey>(userId, SCOPE);
    if (kept) return kept;
    const key = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
    await saveScopeInfo(userId, SCOPE, key);
    // Only a key that was really kept is used: one that can't come back next
    // visit would seal what nobody can open.
    return (await loadScopeInfo<CryptoKey>(userId, SCOPE)) ?? null;
  } catch {
    return null;
  }
}

/** This person's key on this device; null where none can be kept. */
export function restKey(userId: string): Promise<CryptoKey | null> {
  let key = keys.get(userId);
  if (!key) {
    key = loadOrMake(userId);
    keys.set(userId, key);
    // A failure isn't remembered: the next call tries again.
    void key.then((found) => {
      if (!found) keys.delete(userId);
    });
  }
  return key;
}

/** The device store was deleted (signed out or account deleted). */
export function forgetRestKey(userId: string) {
  keys.delete(userId);
}

function base64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function unbase64(text: string): Uint8Array<ArrayBuffer> {
  const binary = atob(text);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Whether a stored value is sealed (old plain values are moved over). */
export function isSealed(value: string): boolean {
  return value.startsWith(PREFIX);
}

/** `text` sealed for this device ("r1.<iv>.<ciphertext>"); null without a key. */
export async function sealText(
  userId: string,
  text: string
): Promise<string | null> {
  const key = await restKey(userId);
  if (!key) return null;
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const sealed = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      new TextEncoder().encode(text)
    )
  );
  return `${PREFIX}${base64(iv)}.${base64(sealed)}`;
}

/** What `sealText` sealed; null if it can't be opened (another key, damaged). */
export async function openText(
  userId: string,
  value: string
): Promise<string | null> {
  if (!isSealed(value)) return null;
  const key = await restKey(userId);
  if (!key) return null;
  const [iv, sealed] = value.slice(PREFIX.length).split('.');
  if (!iv || !sealed) return null;
  try {
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: unbase64(iv) },
      key,
      unbase64(sealed)
    );
    return new TextDecoder().decode(plain);
  } catch {
    return null;
  }
}
